/**
 * ReelProntoPreview · iter r97d · User M1517 · RIFACIMENTO COMPLETO
 *
 * V2 — Reel Instagram/App Preview 1080×1920 punchy stile Apple/Linear/Airbnb:
 *   • Palette dark con gradient mesh (salvia + terracotta) + grain overlay
 *   • Vignette cinematografica sui bordi
 *   • Text: split-word entry con stagger + spring physics + blur→sharp
 *   • iPhone: floating con parallax 3D perspective + breathing hover
 *   • Foto: Ken Burns aggressivo (scale 1.2→1.0 + shift)
 *   • Transizioni: mask reveal diagonal wipe (no crossfade)
 *   • Ritmo fast: 5 scene × 3s = 15s + outro punchy
 *
 * Route: /promo/reel-pronto-preview
 * Registrazione: Playwright viewport 1080×1920, wait 20s, ffmpeg → mp4.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import IPhone17ProFrame from "@/components/IPhone17ProFrame";

// ── Palette moderna ───────────────────────────────────────────────────────
const BG_DARK = "#0F1614";
const SAGE = "#7C8B6E";
const SAGE_LIGHT = "#B4C4A3";
const TERRA = "#CB6D51";
const CREAM = "#FAF7F0";
const INK = "#0F1614";

const SCENE_DURATION = 3.0; // secondi

// ── Timer master ─────────────────────────────────────────────────────────
function useElapsed() {
  const [t, setT] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = () => {
      setT((performance.now() - start) / 1000);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return t;
}

// ── Layer: background mesh gradient + grain ──────────────────────────────
function CinematicBG({ scene }) {
  // Le mesh cambiano leggermente per scena → sensazione di movimento
  const meshes = [
    { c1: SAGE, c2: TERRA, pos: "20% 30%" },
    { c1: SAGE_LIGHT, c2: SAGE, pos: "80% 20%" },
    { c1: TERRA, c2: SAGE, pos: "50% 70%" },
    { c1: SAGE, c2: SAGE_LIGHT, pos: "70% 50%" },
    { c1: TERRA, c2: SAGE, pos: "30% 80%" },
  ];
  const m = meshes[scene % meshes.length];
  return (
    <>
      <motion.div
        key={`mesh-${scene}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 1.2, ease: "easeOut" }}
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse 900px 900px at ${m.pos}, ${m.c1}55 0%, transparent 60%), radial-gradient(ellipse 700px 900px at ${100 - parseInt(m.pos)}% ${parseInt(m.pos.split(" ")[1]) + 30}%, ${m.c2}33 0%, transparent 60%)`,
          filter: "blur(40px)",
        }}
      />
      {/* Grain SVG overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.08,
          mixBlendMode: "overlay",
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* Vignette scuro sui bordi */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse 90% 70% at center, transparent 40%, rgba(0,0,0,0.55) 100%)",
        }}
      />
    </>
  );
}

// ── Split text con stagger word-by-word ──────────────────────────────────
function SplitWords({ text, delay = 0, style = {}, wordClass = "" }) {
  const words = text.split(" ");
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.28em", justifyContent: "center", ...style }}>
      {words.map((w, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{
            delay: delay + i * 0.09,
            duration: 0.65,
            ease: [0.16, 1, 0.3, 1],
          }}
          className={wordClass}
          style={{ display: "inline-block" }}
        >
          {w}
        </motion.span>
      ))}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// SCENA 1 · Intro (0-3s)
// ═════════════════════════════════════════════════════════════════════════
function SceneIntro() {
  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.05, transition: { duration: 0.45, ease: [0.7, 0, 0.84, 0] } }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "80px",
        textAlign: "center",
        color: CREAM,
        pointerEvents: "none",
      }}
    >
      {/* PRONTO label */}
      <motion.div
        initial={{ opacity: 0, letterSpacing: "0.5em" }}
        animate={{ opacity: 0.7, letterSpacing: "0.32em" }}
        transition={{ delay: 0.1, duration: 0.9, ease: "easeOut" }}
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "28px",
          fontWeight: 500,
          marginBottom: "50px",
          color: TERRA,
        }}
      >
        PRONTO
      </motion.div>

      {/* Line 1 (bold sans) */}
      <SplitWords
        text="Parli, scatti."
        delay={0.35}
        style={{
          fontFamily: "'Inter', sans-serif",
          fontWeight: 800,
          fontSize: "132px",
          lineHeight: 0.95,
          letterSpacing: "-0.03em",
          marginBottom: "20px",
        }}
      />

      {/* Line 2 (italic serif) */}
      <SplitWords
        text="L'annuncio è Pronto."
        delay={0.75}
        style={{
          fontFamily: "'Bodoni Moda', serif",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "120px",
          lineHeight: 0.95,
          letterSpacing: "-0.005em",
        }}
      />

      {/* Sub */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 0.85, y: 0 }}
        transition={{ delay: 1.3, duration: 0.7, ease: "easeOut" }}
        style={{
          marginTop: "55px",
          fontFamily: "'Inter', sans-serif",
          fontSize: "32px",
          fontWeight: 400,
          letterSpacing: "-0.005em",
        }}
      >
        Tutto quello che ti serve, in un'unica app.
      </motion.div>

      {/* Accent line terracotta */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 1.7, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{
          marginTop: "30px",
          width: "120px",
          height: "3px",
          background: TERRA,
          transformOrigin: "left",
        }}
      />
    </motion.div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// SCENA 2 · Sopralluogo vocale (3-6s)
// ═════════════════════════════════════════════════════════════════════════
const TRANSCRIPT_LINES = [
  "Trilocale a Brera",
  "Sesto piano, doppia esposizione",
  "Cucina abitabile ristrutturata",
  "Terrazzo di 15 m²",
];

function SceneSopralluogo({ sceneStartAt, t }) {
  const localT = Math.max(0, t - sceneStartAt);
  const pulse = 8 + Math.sin(localT * 8) * 8;

  return (
    <motion.div
      initial={{ opacity: 0, x: 60 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -60, transition: { duration: 0.45, ease: [0.7, 0, 0.84, 0] } }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "80px 60px 0 60px",
        pointerEvents: "none",
      }}
    >
      {/* Scene label */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.5 }}
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "18px",
          fontWeight: 700,
          color: TERRA,
          letterSpacing: "0.32em",
          marginBottom: "16px",
        }}
      >
        01 · SOPRALLUOGO
      </motion.div>

      <SplitWords
        text="Parla, Pronto scrive."
        delay={0.25}
        style={{
          fontFamily: "'Inter', sans-serif",
          fontWeight: 800,
          fontSize: "78px",
          color: CREAM,
          letterSpacing: "-0.02em",
          lineHeight: 1.02,
          textAlign: "center",
        }}
      />

      {/* iPhone flottante con parallax 3D */}
      <motion.div
        initial={{ opacity: 0, y: 80, rotateY: -8, rotateX: 4 }}
        animate={{
          opacity: 1,
          y: [80, 0, -8, 0],
          rotateY: [-8, 0, 2, 0],
          rotateX: [4, 0, -1, 0],
        }}
        transition={{ delay: 0.5, duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
        style={{
          marginTop: "40px",
          transformStyle: "preserve-3d",
          perspective: "1200px",
          transform: "scale(1.6)",
          transformOrigin: "top center",
          width: "400px",
        }}
      >
        <IPhone17ProFrame size="lg" tone="titanium" showIsland={true} shadow={true}>
          <div
            style={{
              width: "100%",
              height: "100%",
              background: CREAM,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              paddingTop: "40px",
              paddingLeft: "18px",
              paddingRight: "18px",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontSize: "9px",
                letterSpacing: "0.28em",
                fontWeight: 700,
                color: TERRA,
                marginBottom: "14px",
              }}
            >
              ● LIVE
            </div>

            {/* Mic pulsante */}
            <div
              style={{
                width: "70px",
                height: "70px",
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${TERRA} 0%, #A85340 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 0 0 ${pulse}px rgba(203,109,81,0.30), 0 8px 20px rgba(203,109,81,0.4)`,
                marginBottom: "22px",
                transition: "box-shadow 0.1s linear",
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                <path d="M12 15a3 3 0 003-3V6a3 3 0 10-6 0v6a3 3 0 003 3zm5-3a5 5 0 01-10 0H5a7 7 0 006 6.92V22h2v-3.08A7 7 0 0019 12h-2z" />
              </svg>
            </div>

            {/* Transcript */}
            <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "7px" }}>
              {TRANSCRIPT_LINES.map((line, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -12, filter: "blur(6px)" }}
                  animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                  transition={{
                    delay: 0.9 + i * 0.35,
                    duration: 0.45,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  style={{
                    background: "white",
                    padding: "9px 13px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: INK,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <span style={{ color: TERRA, marginRight: "5px", fontWeight: 700 }}>›</span>
                  {line}
                </motion.div>
              ))}
            </div>
          </div>
        </IPhone17ProFrame>
      </motion.div>
    </motion.div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// SCENA 3 · Prima/Dopo (6-10s, dura 4s per far vedere lo sweep)
// ═════════════════════════════════════════════════════════════════════════
function ScenePrimaDopo({ sceneStartAt, t }) {
  const localT = Math.max(0, t - sceneStartAt);
  // Slider: 15% → 85% con easing morbido tra 0.8s e 2.8s
  let raw = (localT - 0.8) / 2.0;
  raw = Math.max(0, Math.min(1, raw));
  // Easing "quart"
  const eased = 1 - Math.pow(1 - raw, 4);
  const sliderPos = 15 + eased * 70;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.05, transition: { duration: 0.45, ease: [0.7, 0, 0.84, 0] } }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "80px 60px 0 60px",
        pointerEvents: "none",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.5 }}
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "18px",
          fontWeight: 700,
          color: TERRA,
          letterSpacing: "0.32em",
          marginBottom: "16px",
        }}
      >
        02 · FOTO
      </motion.div>

      <SplitWords
        text="Migliorate in un tap."
        delay={0.25}
        style={{
          fontFamily: "'Inter', sans-serif",
          fontWeight: 800,
          fontSize: "78px",
          color: CREAM,
          letterSpacing: "-0.02em",
          textAlign: "center",
          marginBottom: "40px",
        }}
      />

      {/* Slider prima/dopo con entry mask reveal */}
      <motion.div
        initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
        animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
        transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: "relative",
          width: "860px",
          height: "860px",
          borderRadius: "28px",
          overflow: "hidden",
          boxShadow: "0 40px 100px rgba(0,0,0,0.55)",
        }}
      >
        {/* Zoom sottile Ken Burns su both immagini */}
        <motion.img
          initial={{ scale: 1.08 }}
          animate={{ scale: 1.0 }}
          transition={{ duration: 4, ease: "linear" }}
          src="/landing/sala-original-after.jpg"
          alt="Dopo"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
        <motion.img
          initial={{ scale: 1.08 }}
          animate={{ scale: 1.0 }}
          transition={{ duration: 4, ease: "linear" }}
          src="/landing/sala-original-before.jpg"
          alt="Prima"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            clipPath: `inset(0 ${100 - sliderPos}% 0 0)`,
          }}
        />

        {/* Divider line + handle */}
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${sliderPos}%`,
            width: "3px",
            background: "white",
            boxShadow: "0 0 30px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.5)",
            transform: "translateX(-1.5px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: `${sliderPos}%`,
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            background: "white",
            transform: "translate(-50%, -50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            color: INK,
            fontSize: "24px",
            fontWeight: 900,
          }}
        >
          ‹›
        </div>

        {/* Badges PRIMA / DOPO — bordi arrotondati morbidi */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          style={{
            position: "absolute",
            top: "22px",
            left: "22px",
            background: "rgba(15,22,20,0.85)",
            backdropFilter: "blur(10px)",
            color: "white",
            padding: "11px 22px",
            borderRadius: "12px",
            fontSize: "22px",
            fontWeight: 800,
            letterSpacing: "0.18em",
            fontFamily: "'Inter', sans-serif",
            border: "1px solid rgba(255,255,255,0.15)",
          }}
        >
          PRIMA
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          style={{
            position: "absolute",
            top: "22px",
            right: "22px",
            background: TERRA,
            color: "white",
            padding: "11px 22px",
            borderRadius: "12px",
            fontSize: "22px",
            fontWeight: 800,
            letterSpacing: "0.18em",
            fontFamily: "'Inter', sans-serif",
            boxShadow: "0 6px 20px rgba(203,109,81,0.5)",
          }}
        >
          DOPO
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// SCENA 4 · Contenuti social (10-13s)
// ═════════════════════════════════════════════════════════════════════════
function SceneSocial() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -60, transition: { duration: 0.45, ease: [0.7, 0, 0.84, 0] } }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "80px 60px 0 60px",
        pointerEvents: "none",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.5 }}
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "18px",
          fontWeight: 700,
          color: TERRA,
          letterSpacing: "0.32em",
          marginBottom: "16px",
        }}
      >
        03 · SOCIAL
      </motion.div>

      <SplitWords
        text="Pronto per il feed."
        delay={0.25}
        style={{
          fontFamily: "'Inter', sans-serif",
          fontWeight: 800,
          fontSize: "78px",
          color: CREAM,
          letterSpacing: "-0.02em",
          textAlign: "center",
          marginBottom: "50px",
        }}
      />

      {/* POST + REEL con entry staggered + rotate 3D */}
      <div style={{ display: "flex", gap: "36px", alignItems: "center", perspective: "1400px" }}>
        <motion.div
          initial={{ opacity: 0, y: 80, rotateY: -15 }}
          animate={{ opacity: 1, y: 0, rotateY: -6 }}
          transition={{ delay: 0.55, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          style={{
            width: "540px",
            height: "540px",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
            position: "relative",
            transformStyle: "preserve-3d",
          }}
        >
          <motion.img
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 3, ease: "linear" }}
            src="/landing/social-open-house.v4.jpg"
            alt="Post"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div
            style={{
              position: "absolute",
              top: "18px",
              left: "18px",
              background: TERRA,
              color: "white",
              fontSize: "18px",
              fontWeight: 800,
              letterSpacing: "0.16em",
              padding: "7px 16px",
              borderRadius: "999px",
              boxShadow: "0 6px 16px rgba(203,109,81,0.5)",
            }}
          >
            POST · 1:1
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 80, rotateY: 15 }}
          animate={{ opacity: 1, y: 0, rotateY: 6 }}
          transition={{ delay: 0.7, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          style={{
            width: "320px",
            height: "540px",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
            position: "relative",
            transformStyle: "preserve-3d",
          }}
        >
          <motion.img
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 3, ease: "linear" }}
            src="/landing/social-reel-poster.v4.moderno.jpg"
            alt="Reel"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div
            style={{
              position: "absolute",
              top: "18px",
              left: "18px",
              background: "rgba(15,22,20,0.85)",
              backdropFilter: "blur(10px)",
              color: "white",
              fontSize: "16px",
              fontWeight: 800,
              letterSpacing: "0.16em",
              padding: "6px 14px",
              borderRadius: "999px",
              border: "1px solid rgba(255,255,255,0.15)",
            }}
          >
            REEL · 9:16
          </div>
          <motion.div
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "76px",
              height: "76px",
              borderRadius: "50%",
              background: "rgba(255,255,255,0.95)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            }}
          >
            <svg width="30" height="30" viewBox="0 0 24 24" fill={INK}>
              <polygon points="6,4 20,12 6,20" />
            </svg>
          </motion.div>
        </motion.div>
      </div>

      {/* Icone social row */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.3, duration: 0.6 }}
        style={{
          marginTop: "60px",
          display: "flex",
          gap: "22px",
          alignItems: "center",
        }}
      >
        <div style={{ color: CREAM, fontSize: "20px", fontWeight: 600, letterSpacing: "0.2em", opacity: 0.85 }}>
          PUBBLICA SU
        </div>
        {[
          { bg: "linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)" },
          { bg: "#1877F2" },
          { bg: "#25D366" },
          { bg: "#0A0A0A" },
        ].map((s, i) => (
          <motion.div
            key={i}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1.5 + i * 0.08, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              background: s.bg,
              boxShadow: "0 6px 16px rgba(0,0,0,0.4)",
            }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// SCENA 5 · Outro (13-17s)
// ═════════════════════════════════════════════════════════════════════════
function SceneOutro() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 1.08 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "80px",
        textAlign: "center",
        color: CREAM,
        pointerEvents: "none",
      }}
    >
      {/* Gift emoji con spring bounce */}
      <motion.div
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 220, damping: 12 }}
        style={{ fontSize: "150px", marginBottom: "20px" }}
      >
        🎁
      </motion.div>

      {/* Prova Pronto con */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.6 }}
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "42px",
          fontWeight: 500,
          letterSpacing: "0.02em",
          marginBottom: "8px",
          opacity: 0.9,
        }}
      >
        Prova Pronto con
      </motion.div>

      {/* 60 crediti — Bodoni italic con bounce scale */}
      <motion.div
        initial={{ opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.75, type: "spring", stiffness: 180, damping: 14 }}
        style={{
          fontFamily: "'Bodoni Moda', serif",
          fontStyle: "italic",
          fontSize: "150px",
          fontWeight: 500,
          letterSpacing: "-0.01em",
          lineHeight: 0.95,
          background: `linear-gradient(135deg, ${CREAM} 0%, ${TERRA} 100%)`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        60 crediti gratis
      </motion.div>

      {/* Divider */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 1.15, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{
          marginTop: "50px",
          width: "180px",
          height: "3px",
          background: TERRA,
        }}
      />

      {/* prontoimmo.it */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.35, duration: 0.6 }}
        style={{
          marginTop: "36px",
          fontFamily: "'Inter', sans-serif",
          fontSize: "58px",
          fontWeight: 800,
          letterSpacing: "-0.02em",
        }}
      >
        prontoimmo.it
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        transition={{ delay: 1.55, duration: 0.6 }}
        style={{
          marginTop: "10px",
          fontFamily: "'Inter', sans-serif",
          fontSize: "26px",
          fontWeight: 400,
        }}
      >
        pensata da agenti immobiliari
      </motion.div>
    </motion.div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// MAIN — scene switcher basato sul master timer
// ═════════════════════════════════════════════════════════════════════════
export default function ReelProntoPreview() {
  const t = useElapsed();

  // Scene timeline: 5 scene, ritmo veloce
  // 0-3s  · Intro
  // 3-6s  · Sopralluogo
  // 6-10s · Prima/Dopo (4s per sweep)
  // 10-13s · Social
  // 13-17s · Outro (4s per assorbire)
  let scene = 0;
  let sceneStart = 0;
  if (t < 3) { scene = 0; sceneStart = 0; }
  else if (t < 6) { scene = 1; sceneStart = 3; }
  else if (t < 10) { scene = 2; sceneStart = 6; }
  else if (t < 13) { scene = 3; sceneStart = 10; }
  else { scene = 4; sceneStart = 13; }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,wght@1,400;1,500&family=Inter:wght@300;400;500;600;700;800&display=swap');
        html, body, #root { margin: 0; padding: 0; background: ${BG_DARK}; overflow: hidden; }
        [data-testid="cookie-banner"], .cookie-banner, .CookieConsent,
        [class*="cookie"], [id*="cookie"], [class*="Cookie"] { display: none !important; }
      `}</style>

      <div
        data-testid="reel-pronto-preview"
        data-elapsed={t.toFixed(2)}
        style={{
          width: "1080px",
          height: "1920px",
          background: BG_DARK,
          position: "relative",
          overflow: "hidden",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <CinematicBG scene={scene} />

        <AnimatePresence mode="wait">
          {scene === 0 && <SceneIntro key="s0" />}
          {scene === 1 && <SceneSopralluogo key="s1" sceneStartAt={sceneStart} t={t} />}
          {scene === 2 && <ScenePrimaDopo key="s2" sceneStartAt={sceneStart} t={t} />}
          {scene === 3 && <SceneSocial key="s3" />}
          {scene === 4 && <SceneOutro key="s4" />}
        </AnimatePresence>

        {/* Progress bar terracotta */}
        <div
          style={{
            position: "absolute",
            bottom: "24px",
            left: "60px",
            right: "60px",
            height: "3px",
            background: "rgba(255,255,255,0.10)",
            borderRadius: "999px",
            overflow: "hidden",
            zIndex: 100,
          }}
        >
          <motion.div
            initial={{ width: "0%" }}
            animate={{ width: `${Math.min(100, (t / 17) * 100)}%` }}
            transition={{ duration: 0.1, ease: "linear" }}
            style={{
              height: "100%",
              background: TERRA,
              boxShadow: `0 0 12px ${TERRA}`,
            }}
          />
        </div>
      </div>
    </>
  );
}
